import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { usePermissions } from '../../auth/store';
import { errMsg } from '../../lib/errors';
import { formatRupees } from '../../lib/money';
import { QueryError } from '../../lib/QueryState';
import { formatBps, percentToBps, type AgencyDetail } from './agencyApi';
import {
  AgencyCommissionsTable,
  AgencyRechargesTable,
  AgencyUsersTable,
  Amounts,
  BalanceCards,
  payoutColumns,
  when,
} from './agencyViews';
import { useAgencyCommissions, useAgencyPayouts, useAgencyRecharges, useAgencyUsers } from './agencyLists';
import { LoadMore } from '../../lib/useCursorPages';

const TABS = ['overview', 'users', 'recharges', 'commission', 'payouts'] as const;

export function AgencyDetailPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as string) : 'overview';
  const query = useQuery({
    queryKey: ['admin', 'agencies', 'detail', id],
    queryFn: async () => (await api.get<AgencyDetail>(`/admin/agencies/${id}`)).data,
  });
  const a = query.data;
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Link to="/agencies">
        <ArrowLeftOutlined /> Agencies
      </Link>
      {query.isError && <QueryError error={query.error} what="the agency" onRetry={() => void query.refetch()} />}
      {a && (
        <>
          <Space wrap align="center">
            <Typography.Title level={3} style={{ margin: 0 }}>
              {a.name}
            </Typography.Title>
            <Typography.Text code copyable>
              {a.code}
            </Typography.Text>
            <Tag color={a.status === 'active' ? 'green' : 'red'}>{a.status === 'active' ? 'Active' : 'Suspended'}</Tag>
          </Space>
          <Tabs
            activeKey={tab}
            onChange={(k) => setParams({ tab: k }, { replace: true })}
            items={[
              {
                key: 'overview',
                label: 'Overview',
                children: <Overview a={a} />,
              },
              {
                key: 'users',
                label: `Users (${a.users.toLocaleString('en-IN')})`,
                children: <UsersTab id={id} />,
              },
              {
                key: 'recharges',
                label: 'Recharges',
                children: <RechargesTab id={id} />,
              },
              {
                key: 'commission',
                label: 'Commission',
                children: <CommissionTab id={id} />,
              },
              {
                key: 'payouts',
                label: 'Payouts',
                children: <PayoutsTab a={a} />,
              },
            ]}
          />
        </>
      )}
    </Space>
  );
}

function useInvalidate(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'agencies'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'agency-lists'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'agency-payouts'] });
    void qc.invalidateQueries({
      queryKey: ['admin', 'agencies', 'detail', id],
    });
  };
}

function Overview({ a }: { a: AgencyDetail }) {
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Users" value={a.users} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Paying users" value={a.payingUsers} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Typography.Text type="secondary">Commission earned</Typography.Text>
            <div style={{ fontSize: 20 }}>
              <Amounts rows={a.commission.accrued} />
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Typography.Text type="secondary">Reversed (refunds)</Typography.Text>
            <div style={{ fontSize: 20 }}>
              <Amounts rows={a.commission.reversed} />
            </div>
          </Card>
        </Col>
      </Row>
      <BalanceCards balance={a.balance} />
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <CommissionCard a={a} />
        </Col>
        <Col xs={24} lg={12}>
          <LoginCard a={a} />
        </Col>
        <Col xs={24} lg={12}>
          <PortalControlsCard a={a} />
        </Col>
        <Col xs={24} lg={12}>
          <DetailsCard a={a} />
        </Col>
        <Col xs={24} lg={12}>
          <PayoutDetailsCard a={a} />
        </Col>
      </Row>
    </Space>
  );
}

interface CommissionForm {
  mode: 'default' | 'custom';
  pct?: number;
  windowMode: 'default' | 'custom';
  windowDays?: number;
  enabled: boolean;
}

interface PayoutDetailsForm {
  payoutMethod: 'bank' | 'upi';
  accountHolderName?: string;
  bankName?: string;
  accountNumber?: string;
  ifsc?: string;
  upiId?: string;
  pan?: string;
}

function CommissionCard({ a }: { a: AgencyDetail }) {
  const { message } = App.useApp();
  const invalidate = useInvalidate(a.id);
  const [form] = Form.useForm<CommissionForm>();
  const mode = Form.useWatch('mode', form);
  const windowMode = Form.useWatch('windowMode', form);
  const save = useMutation({
    mutationFn: (v: CommissionForm) =>
      api.put(`/admin/agencies/${a.id}/commission`, {
        commissionBps: v.mode === 'default' ? null : percentToBps(v.pct ?? 0),
        commissionWindowDays: v.windowMode === 'default' ? null : (v.windowDays ?? 0),
        commissionEnabled: v.enabled,
      }),
    onSuccess: () => {
      message.success('Commission updated — applies to recharges from now on');
      invalidate();
    },
    onError: (e) => message.error(errMsg(e, 'Could not update commission')),
  });
  return (
    <Card size="small" title="Commission">
      <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Rate in force">
          {a.commissionEnabled ? formatBps(a.effectiveCommissionBps) : <Tag>Off</Tag>}
          {a.commissionBps === null && <Typography.Text type="secondary"> (default)</Typography.Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Window">
          {a.effectiveWindowDays === 0 ? 'For life' : `${a.effectiveWindowDays} days after joining`}
          {a.commissionWindowDays === null && <Typography.Text type="secondary"> (default)</Typography.Text>}
        </Descriptions.Item>
      </Descriptions>
      <Can permission="agencies.commission.edit">
        <Form<CommissionForm>
          form={form}
          layout="vertical"
          initialValues={{
            mode: a.commissionBps === null ? 'default' : 'custom',
            pct: (a.commissionBps ?? a.effectiveCommissionBps) / 100,
            windowMode: a.commissionWindowDays === null ? 'default' : 'custom',
            windowDays: a.commissionWindowDays ?? a.effectiveWindowDays,
            enabled: a.commissionEnabled,
          }}
          onFinish={(v) => save.mutate(v)}
        >
          <Form.Item name="mode" label="Rate">
            <Radio.Group
              options={[
                { value: 'default', label: 'Use default' },
                { value: 'custom', label: 'Custom' },
              ]}
            />
          </Form.Item>
          {mode === 'custom' && (
            <Form.Item name="pct" label="Commission %" rules={[{ required: true }]}>
              <InputNumber min={0} max={50} step={0.5} addonAfter="%" />
            </Form.Item>
          )}
          <Form.Item name="windowMode" label="Window">
            <Radio.Group
              options={[
                { value: 'default', label: 'Use default' },
                { value: 'custom', label: 'Custom' },
              ]}
            />
          </Form.Item>
          {windowMode === 'custom' && (
            <Form.Item name="windowDays" label="Days after joining (0 = for life)" rules={[{ required: true }]}>
              <InputNumber min={0} max={3650} />
            </Form.Item>
          )}
          <Form.Item name="enabled" label="Commission on" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={save.isPending}>
            Save commission
          </Button>
        </Form>
      </Can>
      {a.rateChanges.length > 0 && (
        <>
          <Typography.Title level={5} style={{ marginTop: 16 }}>
            Rate history
          </Typography.Title>
          <Table
            size="small"
            rowKey={(r) => r.createdAt}
            pagination={false}
            dataSource={a.rateChanges}
            columns={[
              {
                title: 'When',
                dataIndex: 'createdAt',
                render: (v: string) => when(v),
              },
              {
                title: 'Change',
                key: 'c',
                render: (_, r) =>
                  `${r.oldBps === null ? 'default' : formatBps(r.oldBps)} → ${r.newBps === null ? 'default' : formatBps(r.newBps)}`,
              },
              {
                title: 'By',
                dataIndex: 'changedBy',
                render: (v: string | null) => v ?? '—',
              },
            ]}
          />
        </>
      )}
    </Card>
  );
}

function LoginCard({ a }: { a: AgencyDetail }) {
  const { message, modal } = App.useApp();
  const invalidate = useInvalidate(a.id);
  const [email, setEmail] = useState(a.login?.email ?? a.contactEmail ?? '');
  const create = useMutation({
    mutationFn: async () =>
      (await api.post<{ email: string; generatedPassword: string }>(`/admin/agencies/${a.id}/login`, { email })).data,
    onSuccess: (r) => {
      invalidate();
      modal.success({
        title: 'Portal login ready',
        content: (
          <Space direction="vertical">
            <span>Share these with the agency. The password is shown only once.</span>
            <Typography.Text copyable>{r.email}</Typography.Text>
            <Typography.Text copyable code>
              {r.generatedPassword}
            </Typography.Text>
          </Space>
        ),
      });
    },
    onError: (e) => message.error(errMsg(e, 'Could not create the login')),
  });
  return (
    <Card size="small" title="Agency portal login">
      <Typography.Paragraph type="secondary">
        The agency signs in to this admin panel and sees only its own users, their recharges, its commission and payouts — never
        phone numbers, chats, calls or ID documents.
      </Typography.Paragraph>
      {a.login ? (
        <Typography.Paragraph>
          Login: <strong>{a.login.email}</strong> {a.login.disabled && <Tag color="red">Disabled (agency suspended)</Tag>}
        </Typography.Paragraph>
      ) : (
        <Alert type="warning" showIcon message="No portal login yet" style={{ marginBottom: 12 }} />
      )}
      <Can permission="agencies.login.manage">
        <Space.Compact style={{ width: '100%' }}>
          <Input placeholder="Login email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Popconfirm
            title={a.login ? 'Reset the password? The agency is signed out everywhere.' : 'Create the portal login?'}
            onConfirm={() => create.mutate()}
          >
            <Button type="primary" loading={create.isPending} disabled={!email.trim()}>
              {a.login ? 'Reset password' : 'Create login'}
            </Button>
          </Popconfirm>
        </Space.Compact>
      </Can>
    </Card>
  );
}

/**
 * Show / hide what the agency can DO in its portal. Off = it only views its
 * listeners and users. The API refuses the action too, so this is not just a
 * hidden button.
 */
function PortalControlsCard({ a }: { a: AgencyDetail }) {
  const { message } = App.useApp();
  const invalidate = useInvalidate(a.id);
  const { can } = usePermissions();
  const editable = can('agencies.edit');
  const controls = a.controls ?? { listeners: false, users: false };
  const save = useMutation({
    mutationFn: async (body: { listenerControls?: boolean; userControls?: boolean }) =>
      (await api.put(`/admin/agencies/${a.id}/controls`, body)).data as unknown,
    onSuccess: () => {
      message.success('Portal controls updated');
      invalidate();
    },
    onError: (e) => message.error(errMsg(e, 'Could not update the portal controls')),
  });
  const pending = (k: 'listenerControls' | 'userControls') => save.isPending && save.variables?.[k] !== undefined;
  const row = (label: string, help: string, checked: boolean, k: 'listenerControls' | 'userControls') => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
      <div>
        <Typography.Text strong>{label}</Typography.Text>
        <br />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {help}
        </Typography.Text>
      </div>
      <Switch
        checked={checked}
        disabled={!editable}
        loading={pending(k)}
        checkedChildren="Shown"
        unCheckedChildren="Hidden"
        onChange={(v) => save.mutate({ [k]: v })}
      />
    </div>
  );
  return (
    <Card size="small" title="Agency portal controls">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text type="secondary">
          What this agency can do with the people who joined with its code. Hidden = it can only view them.
        </Typography.Text>
        {row(
          'Listener controls',
          'Approve or reject its own listener applications (final say — an approved listener goes live).',
          controls.listeners,
          'listenerControls',
        )}
        {row('User controls', 'Block or unblock its own users and listeners.', controls.users, 'userControls')}
      </Space>
    </Card>
  );
}

function DetailsCard({ a }: { a: AgencyDetail }) {
  const { message } = App.useApp();
  const invalidate = useInvalidate(a.id);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const save = useMutation({
    mutationFn: (v: Record<string, unknown>) => api.patch(`/admin/agencies/${a.id}`, v),
    onSuccess: () => {
      message.success('Saved');
      setEditing(false);
      invalidate();
    },
    onError: (e) => message.error(errMsg(e, 'Could not save')),
  });
  return (
    <Card
      size="small"
      title="Details"
      extra={
        <Can permission="agencies.edit">
          <Space>
            <Popconfirm
              title={
                a.status === 'active'
                  ? 'Suspend? New recharges earn no commission and the portal login is disabled.'
                  : 'Reactivate this agency?'
              }
              onConfirm={() =>
                save.mutate({
                  status: a.status === 'active' ? 'suspended' : 'active',
                })
              }
            >
              <Button size="small" danger={a.status === 'active'}>
                {a.status === 'active' ? 'Suspend' : 'Reactivate'}
              </Button>
            </Popconfirm>
            <Button size="small" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </Space>
        </Can>
      }
    >
      <Descriptions size="small" column={1}>
        <Descriptions.Item label="Contact">{a.contactName ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Phone">{a.contactPhone ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Email">{a.contactEmail ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Invite link">
          <Typography.Text copyable>{`https://dudesocial.in/r/${a.code}`}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Created">{when(a.createdAt)}</Descriptions.Item>
        <Descriptions.Item label="Notes">{a.notes ?? '—'}</Descriptions.Item>
      </Descriptions>
      <Modal
        open={editing}
        title="Edit agency"
        onCancel={() => setEditing(false)}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={a}
          onFinish={(v: Record<string, string>) =>
            save.mutate({
              name: v.name,
              contactName: v.contactName || null,
              contactPhone: v.contactPhone || null,
              contactEmail: v.contactEmail || null,
              notes: v.notes || null,
            })
          }
        >
          <Form.Item name="name" label="Name" rules={[{ required: true, min: 2 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="contactName" label="Contact person">
            <Input />
          </Form.Item>
          <Form.Item name="contactPhone" label="Contact phone">
            <Input />
          </Form.Item>
          <Form.Item name="contactEmail" label="Contact email" rules={[{ type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

function PayoutDetailsCard({ a }: { a: AgencyDetail }) {
  const { message } = App.useApp();
  const invalidate = useInvalidate(a.id);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm<PayoutDetailsForm>();
  const method = Form.useWatch('payoutMethod', form);
  const d = a.payoutDetails;
  const save = useMutation({
    mutationFn: (v: PayoutDetailsForm) =>
      api.patch(`/admin/agencies/${a.id}`, {
        payoutMethod: v.payoutMethod,
        accountHolderName: v.accountHolderName || null,
        bankName: v.payoutMethod === 'bank' ? v.bankName || null : null,
        accountNumber: v.payoutMethod === 'bank' ? v.accountNumber || null : null,
        ifsc: v.payoutMethod === 'bank' ? v.ifsc || null : null,
        upiId: v.payoutMethod === 'upi' ? v.upiId || null : null,
        pan: v.pan || null,
      }),
    onSuccess: () => {
      message.success('Payout details saved');
      setEditing(false);
      invalidate();
    },
    onError: (e) => message.error(errMsg(e, 'Could not save payout details')),
  });
  return (
    <Card
      size="small"
      title="Payout details"
      extra={
        <Can permission="agencies.edit">
          <Button size="small" onClick={() => setEditing(true)}>
            Edit
          </Button>
        </Can>
      }
    >
      {!d.payoutMethod ? (
        <Alert type="warning" showIcon message="No payout details — payouts can't be created until they are added." />
      ) : (
        <Descriptions size="small" column={1}>
          <Descriptions.Item label="Method">{d.payoutMethod === 'bank' ? 'Bank transfer' : 'UPI'}</Descriptions.Item>
          <Descriptions.Item label="Name">{d.accountHolderName ?? '—'}</Descriptions.Item>
          {d.payoutMethod === 'bank' ? (
            <>
              <Descriptions.Item label="Bank">{d.bankName ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Account">{d.accountNumber}</Descriptions.Item>
              <Descriptions.Item label="IFSC">{d.ifsc}</Descriptions.Item>
            </>
          ) : (
            <Descriptions.Item label="UPI ID">{d.upiId}</Descriptions.Item>
          )}
          <Descriptions.Item label="PAN">{d.pan ?? '—'}</Descriptions.Item>
        </Descriptions>
      )}
      <Modal
        open={editing}
        title="Payout details"
        onCancel={() => setEditing(false)}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
        destroyOnClose
      >
        <Form<PayoutDetailsForm>
          form={form}
          layout="vertical"
          initialValues={{ payoutMethod: 'bank', ...stripNulls(d) }}
          onFinish={(v) => save.mutate(v)}
        >
          <Form.Item name="payoutMethod" label="Method">
            <Radio.Group
              options={[
                { value: 'bank', label: 'Bank transfer' },
                { value: 'upi', label: 'UPI' },
              ]}
            />
          </Form.Item>
          <Form.Item name="accountHolderName" label="Account holder name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          {method !== 'upi' ? (
            <>
              <Form.Item name="bankName" label="Bank name">
                <Input />
              </Form.Item>
              <Form.Item name="accountNumber" label="Account number" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="ifsc" label="IFSC" rules={[{ required: true }]}>
                <Input style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </>
          ) : (
            <Form.Item name="upiId" label="UPI ID" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          <Form.Item name="pan" label="PAN (for TDS)">
            <Input style={{ textTransform: 'uppercase' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

function stripNulls<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== null)) as Partial<T>;
}

function UsersTab({ id }: { id: string }) {
  const list = useAgencyUsers(`/admin/agencies/${id}`);
  if (list.isError) return <QueryError error={list.error} what="users" onRetry={list.refetch} />;
  return <AgencyUsersTable list={list} rows={list.rows} linkUsers />;
}

function RechargesTab({ id }: { id: string }) {
  const list = useAgencyRecharges(`/admin/agencies/${id}`);
  if (list.isError) return <QueryError error={list.error} what="recharges" onRetry={list.refetch} />;
  return <AgencyRechargesTable list={list} rows={list.rows} linkUsers />;
}

function CommissionTab({ id }: { id: string }) {
  const list = useAgencyCommissions(`/admin/agencies/${id}`);
  if (list.isError) return <QueryError error={list.error} what="commission" onRetry={list.refetch} />;
  return <AgencyCommissionsTable list={list} rows={list.rows} linkUsers />;
}

function PayoutsTab({ a }: { a: AgencyDetail }) {
  const { message } = App.useApp();
  const invalidate = useInvalidate(a.id);
  const list = useAgencyPayouts('/admin/agency-payouts', { agencyId: a.id });
  const create = useMutation({
    mutationFn: () => api.post('/admin/agency-payouts', { agencyId: a.id }),
    onSuccess: () => {
      message.success('Payout created — approve it in Agency payouts');
      invalidate();
    },
    onError: (e) => message.error(errMsg(e, 'Could not create the payout')),
  });
  const canPay =
    !a.balance.openPayoutId &&
    Number(a.balance.payablePaise) > 0 &&
    Number(a.balance.payablePaise) >= Number(a.balance.minPayoutPaise) &&
    !!a.payoutDetails.payoutMethod;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BalanceCards balance={a.balance} />
      <Can permission="agency_payouts.create">
        <Popconfirm
          title={`Create a payout of ${formatRupees(a.balance.payablePaise)}?`}
          onConfirm={() => create.mutate()}
          disabled={!canPay}
        >
          <Button type="primary" disabled={!canPay} loading={create.isPending}>
            Pay out {formatRupees(a.balance.payablePaise)}
          </Button>
        </Popconfirm>
        {!canPay && (
          <Typography.Text type="secondary" style={{ marginLeft: 12 }}>
            {a.balance.openPayoutId
              ? 'A payout is already in progress.'
              : !a.payoutDetails.payoutMethod
                ? 'Add payout details first.'
                : 'Payable balance is below the minimum payout.'}
          </Typography.Text>
        )}
      </Can>
      {list.isError ? (
        <QueryError error={list.error} what="payouts" onRetry={list.refetch} />
      ) : (
        <>
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            columns={payoutColumns()}
            dataSource={list.rows}
            pagination={false}
            scroll={{ x: 900 }}
          />
          <LoadMore list={list} noun="payouts" />
        </>
      )}
    </Space>
  );
}
